/* eslint-disable no-restricted-syntax */
import React, { useEffect, useState } from 'react';
import dayjs from 'utils/dayjs';
import { compact, flatten, get, groupBy, isEmpty, isEqual, meanBy, orderBy, sumBy, uniq } from 'lodash';
// import ReactECharts from 'echarts-for-react';
import ReactECharts from './components/ReactECharts';
import { X_AXIS_TYPE, Y_AXIS_TYPE, CHART_TYPE, TOTAL_TYPE } from './constants';
import { filterData, updateObjectWithStats } from './utils';

const Graph = ({
    data,
    possiblyAddFilter,
    removeFilter,
    filters = [],
    grouping,
    xAxisType,
    yAxisType,
    currency,
    filterProperty,
    filterDateFormat,
    filterLabel,
    fetching,
    fetched,
    graphTitle,
    yAxisLabel,
    chartType,
    groupOnResData = false,
    series,
    lySpitPathToData,
    lyFinalPathToData,
    budgetPathToData,
    totalType,
}) => {
    const colours = ['#2cbcfa', '#4fd8bf', '#fa877e', '#fad37a', '#f6a7c1', '#b28dff', '#97f2f3', '#f0a35e'];
    const [eChartOptions, setEChartOptions] = useState({});

    const addFilter = (equals) => {
        const filter = {
            property: filterProperty,
            equals: xAxisType === X_AXIS_TYPE.NUMBER ? parseInt(equals, 10) : equals,
            dateFormat: filterDateFormat,
            grouping,
            filterResData: groupOnResData,
            label: filterLabel,
        };
        const filterExists = filters.some((f) => isEqual(f, filter));
        if (filterExists) {
            removeFilter(filter);
        } else {
            possiblyAddFilter(filter);
        }
    };

    const getChartData = (rawData, pathToData) => {
        if (groupOnResData) {
            const dataRequired = rawData.map((d) => get(d, pathToData));
            return Object.values(
                flatten(dataRequired).reduce((acc, d) => {
                    (acc[d?.name] ??= { name: d?.name, value: 0 }).value += get(d, 'stats.rooms', 0);
                    return acc;
                }, {}),
            );
        }
        return Object.entries(rawData).map(([key, value]) => {
            if (filters.some((f) => f.grouping === grouping)) {
                if (filters.some((f) => f.equals === key)) {
                    return totalType === TOTAL_TYPE.AVERAGE ? meanBy(value, pathToData) : sumBy(value, pathToData);
                }
                return {
                    value: totalType === TOTAL_TYPE.AVERAGE ? meanBy(value, pathToData) : sumBy(value, pathToData),
                    itemStyle: {
                        opacity: '0.1',
                    },
                };
            }
            return totalType === TOTAL_TYPE.AVERAGE ? meanBy(value, pathToData) : sumBy(value, pathToData);
        });
    };

    const getXAxisValues = (rawData) => Object.keys(rawData).map((key) => key);

    const workoutChartOptions = (chartData, chartDataUsingLYStayDate) => {
        const format = (d) => {
            if (yAxisType === Y_AXIS_TYPE.CURRENCY && currency && d && d > 0) {
                const dataFormatted = parseFloat(d / 100);
                return dataFormatted.toLocaleString('en-GB', { style: 'currency', currency });
            }
            if (yAxisType === Y_AXIS_TYPE.PERCENT && d) {
                const dataFormatted = Math.floor(d * 100);
                return `${dataFormatted} %`;
            }
            if (!d) {
                return ' - ';
            }
            return d;
        };

        const barChartOptions = {
            xAxis: {
                type: 'category',
                data: getXAxisValues(chartData),
            },
            yAxis: {
                type: 'value',
                name: yAxisLabel,
                nameLocation: 'center',
                nameGap: 100,
                axisLabel: {
                    formatter: (val) => format(val),
                },
            },
            legend: {
                top: 30, // the size of title + margin
                left: 'left', // or 0 or '0%'
            },
            grid: {
                top: 80, // the size of title + legend + margin
                containLabel: true,
                left: 0,
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow',
                },
                valueFormatter: (val) => format(val),
            },
        };

        let radius = null;
        switch (chartType) {
            case CHART_TYPE.PIE:
                radius = ['70%'];
                break;
            case CHART_TYPE.DOUGHNUT:
                radius = ['40%', '70%'];
                break;
            default:
                radius = null;
        }

        const seriesArray = series.map((s, i) => ({
            name: s.legendName,
            data: getChartData(chartData, s.pathToData),
            type: chartType === CHART_TYPE.DOUGHNUT ? 'pie' : chartType,
            radius,
            center: chartType === CHART_TYPE.PIE || chartType === CHART_TYPE.DOUGHNUT ? ['40%', '50%'] : null,
            animationDelay: (idx) => idx * 100,
            label: {
                show: false,
                position: 'center',
            },
            labelLine: {
                show: false,
            },
            // itemStyle: chartType === CHART_TYPE.BAR ? { normal: { barBorderRadius: 4 } } : null,
            stack: 'stacked',
            color: colours.slice(i),
        }));

        if (lySpitPathToData && !filters.some((f) => f.property !== 'stayDate')) {
            seriesArray.push({
                name: 'LYSPIT',
                data: getChartData(chartDataUsingLYStayDate, lySpitPathToData),
                type: 'line',
                animationDelay: (idx) => idx * 100,
                lineStyle: { color: '#fac758', type: 'dashed', width: 3 },
                itemStyle: { color: '#fac758' },
            });
        }

        if (lyFinalPathToData) {
            seriesArray.push({
                name: 'LYFINAL',
                data: getChartData(chartDataUsingLYStayDate, lyFinalPathToData),
                type: 'line',
                animationDelay: (idx) => idx * 100,
                lineStyle: { color: '#ef6766', type: 'dotted', width: 3 },
                itemStyle: { color: '#ef6766' },
                symbol: 'triangle',
                symbolSize: 6,
            });
        }

        if (budgetPathToData) {
            seriesArray.push({
                name: 'Budget',
                data: getChartData(chartData, budgetPathToData),
                type: 'line',
                animationDelay: (idx) => idx * 100,
                lineStyle: { color: '#b28dff', type: 'dashed', width: 3, dashOffset: 1 },
                itemStyle: { color: '#b28dff' },
                symbol: 'diamond',
                symbolSize: 6,
            });
        }

        const options = {
            title: {
                text: graphTitle,
            },
            series: seriesArray,
            animationDurationUpdate: 500,
            animationEasing: 'elasticOut',
            animationDelayUpdate: (idx) => idx * 100,
        };

        if (chartType === CHART_TYPE.PIE || chartType === CHART_TYPE.DOUGHNUT) {
            return {
                ...options,
                emphasis: {
                    itemStyle: {
                        shadowBlur: 10,
                        shadowOffsetX: 0,
                        shadowColor: 'rgba(0, 0, 0, 0.5)',
                    },
                },
                tooltip: {
                    trigger: 'item',
                },
                legend: {
                    orient: 'vertical',
                    type: 'scroll',
                    right: 0,
                    top: 20,
                    bottom: 20,
                    textStyle: {
                        overflow: 'truncate',
                        width: 100,
                    },
                    // data: !isEmpty(seriesData) ? uniq(compact(seriesData.map(({ name }) => name))) : null,
                },
            };
        }

        if (chartType === CHART_TYPE.TREEMAP) {
            return {
                ...options,
                series: [
                    {
                        type: options.series[0].type,
                        data: options.series[0].data,
                        roam: 'move',
                        nodeClick: false,
                        breadcrumb: {
                            show: false,
                        },
                    },
                ],
                color: colours,
            };
        }

        if (chartType === CHART_TYPE.SUNBURST) {
            return {
                ...options,
                series: {
                    type: options.series[0].type,
                    data: options.series[0].data,
                    radius: [0, '90%'],
                    label: {
                        rotate: 'radial',
                    },
                    color: colours,
                },
            };
        }

        if (chartType === CHART_TYPE.LINE || (chartType === CHART_TYPE.BAR && filterProperty !== 'stayDate')) {
            return {
                ...options,
                xAxis: {
                    type: 'category',
                    data: !isEmpty(seriesArray[0]?.data)
                        ? uniq(compact(seriesArray[0].data.map(({ name }) => name))).filter(
                              (item) => item !== 'undefined',
                          )
                        : null,
                },
                yAxis: {
                    type: 'value',
                },
                tooltip: {
                    trigger: 'axis',
                },
            };
        }

        return {
            ...options,
            ...barChartOptions,
        };
    };

    const formatData = () => {
        let filteredData = [...data];
        const lyCalculation = filters.find((f) => f.grouping === 'ddd') ? 'week' : 'year';
        filteredData = updateObjectWithStats(
            filterData(
                data,
                filters.filter((f) => f.grouping !== grouping && !f.filterResData),
            ),
            filters.filter((f) => f.filterResData && f.grouping !== grouping),
            lyCalculation,
        );

        let filteredDataForLYStayDate = [...filteredData];

        if (grouping === 'ddd') {
            filteredData = orderBy(filteredData, [(d) => dayjs(d.stayDate).format('d')]);
            filteredDataForLYStayDate = orderBy(filteredDataForLYStayDate, [(d) => dayjs(d.lyStayDate).format('d')]);
        }
        let formatted;
        let formattedWithLYStayDate;
        if (groupOnResData) {
            formatted = filteredData;
            formattedWithLYStayDate = filteredData;
        } else {
            formatted = groupBy(
                filteredData,
                xAxisType === X_AXIS_TYPE.DATE ? ({ stayDate }) => dayjs(stayDate).format(grouping) : grouping,
            );
            formattedWithLYStayDate = groupBy(
                filteredDataForLYStayDate,
                xAxisType === X_AXIS_TYPE.DATE ? ({ lyStayDate }) => dayjs(lyStayDate).format(grouping) : grouping,
            );
        }
        const blah = workoutChartOptions(formatted, formattedWithLYStayDate);

        setEChartOptions(blah);
    };

    const onChartClick = (params) => addFilter(params.name);

    useEffect(() => {
        formatData();
    }, [filters, data]);

    return (
        <ReactECharts
            option={eChartOptions}
            onEvent={onChartClick}
            loading={!fetched && fetching}
            style={{ height: '300px', width: '100%', marginBottom: '-30px' }}
        />
    );
};

export default Graph;
